import Carousel, { useCarousel } from '@orn/shared-web/components/Carousel';
import { PaginationDot } from '@orn/shared-web/components/Pagination';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { useContainerWidth } from '../../hooks/useContainerWidth';
import CalloutCard, { CALLOUT_CARD_HEIGHT } from '../CalloutCard';

import type { Callout } from '../CalloutCard';

/** Default edge padding — matches Entity.Body's `px-8` gutter (its conventional host). */
const DEFAULT_EDGE_PADDING_PX = 32;

interface ICalloutCarouselProps {
  callouts: Callout[];
  onDismiss?: (calloutId: string, appropriate?: boolean) => void;
  className?: string;
  onLoadMore?: () => void | Promise<void>;
  showLoadMore?: boolean;
  isLoadingMore?: boolean;
  onCalloutClick?: (callout: Callout) => void;
  /**
   * Horizontal padding (in px) inside each carousel item — determines the inset
   * of the callout cards from their host container's edge. Default
   * {@link DEFAULT_EDGE_PADDING_PX} (32) matches Entity.Body's `px-8`. Pass a
   * smaller value when the carousel is embedded in a narrower container
   * (e.g. a bounded card with `px-6`).
   */
  edgePadding?: number;
  /**
   * When true (default), the outer wrapper applies `-mx-8 w-[calc(100%+4rem)]`
   * so the carousel escapes Entity.Body's `px-8` gutter and extends to the
   * viewport edge. Set to `false` when rendering inside a bounded container
   * that does not sit inside a `px-8` parent — the wrapper becomes `w-full`.
   */
  bleed?: boolean;
}

const CARD_GAP = 24;
const MIN_CARD_WIDTH = 300;
const MAX_VISIBLE_CARDS = 3;

interface ICarouselIndicatorProps {
  className?: string;
  showLoadMore?: boolean;
}

const CarouselIndicator = ({ className, showLoadMore = false }: ICarouselIndicatorProps) => {
  const { selectedIndex, scrollSnaps, api } = useCarousel();

  const totalPages = scrollSnaps.length + (showLoadMore ? 1 : 0);

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className={className}>
      <PaginationDot
        size="md"
        page={selectedIndex + 1}
        total={totalPages}
        onPageChange={(page) => {
          if (showLoadMore && page === totalPages) {
            return;
          }
          api?.scrollTo(page - 1);
        }}
      />
    </div>
  );
};

interface ICalloutCarouselContentProps {
  onDismiss?: (calloutId: string, appropriate?: boolean) => void;
  showLoadMore: boolean;
  pages: Callout[][];
  containerWidth: number;
  edgePadding: number;
  cardGap: number;
  /** Returns card width for a given page based on number of cards on that page (allows 2 cards to fill full width) */
  getCardWidthForPage: (cardsOnPage: number) => number;
  onCalloutClick?: (callout: Callout) => void;
}

const CalloutCarouselContent = ({
  onDismiss,
  showLoadMore,
  pages,
  containerWidth,
  edgePadding,
  cardGap,
  getCardWidthForPage,
  onCalloutClick,
}: ICalloutCarouselContentProps) => {
  return (
    <>
      <Carousel.Content className="w-full gap-0">
        {pages.map((pageCallouts, pageIndex) => (
          <Carousel.Item key={pageIndex} className="shrink-0" style={{ width: containerWidth }}>
            <div style={{ paddingLeft: edgePadding, paddingRight: edgePadding }}>
              <div className="flex" style={{ gap: cardGap }}>
                {pageCallouts.map((callout) => (
                  <div
                    key={callout.id}
                    className="shrink-0"
                    style={{
                      width: getCardWidthForPage(pageCallouts.length),
                      height: CALLOUT_CARD_HEIGHT,
                    }}
                  >
                    <CalloutCard
                      callout={callout}
                      onDismiss={onDismiss ? (appropriate?: boolean) => onDismiss(callout.id, appropriate) : undefined}
                      onClick={onCalloutClick ? () => onCalloutClick(callout) : undefined}
                      className="h-full w-full"
                    />
                  </div>
                ))}
              </div>
            </div>
          </Carousel.Item>
        ))}
      </Carousel.Content>

      <CarouselIndicator showLoadMore={showLoadMore} />
    </>
  );
};

const CalloutCarouselBase = ({
  callouts,
  onDismiss,
  className,
  onLoadMore,
  showLoadMore = false,
  isLoadingMore = false,
  onCalloutClick,
  edgePadding = DEFAULT_EDGE_PADDING_PX,
  bleed = true,
}: ICalloutCarouselProps) => {
  const { containerWidth: measuredWidth, containerRef } = useContainerWidth<HTMLDivElement>();

  // Calculate how many cards can fit based on container width
  const containerWidth = measuredWidth;
  const availableWidth = containerWidth - edgePadding * 2;
  const calculatedCards = Math.floor((availableWidth + CARD_GAP) / (MIN_CARD_WIDTH + CARD_GAP));
  const visibleCards = Math.max(1, Math.min(MAX_VISIBLE_CARDS, calculatedCards));

  // Calculate card width for a page.
  // When all callouts fit on a single page, size cards to fill the full width.
  // When there are multiple pages, use consistent visibleCards-based width so cards
  // don't change size as you scroll (e.g. a last page with 2 of 3 cards).
  const isSinglePage = callouts.length <= visibleCards;
  const getCardWidthForPage = (cardsOnPage: number) => {
    const effectiveCount = isSinglePage ? cardsOnPage : visibleCards;
    return (availableWidth - (effectiveCount - 1) * CARD_GAP) / effectiveCount;
  };

  // Group callouts into pages
  const pages: Callout[][] = React.useMemo(() => {
    const result: Callout[][] = [];
    for (let i = 0; i < callouts.length; i += visibleCards) {
      result.push(callouts.slice(i, i + visibleCards));
    }
    return result;
  }, [callouts, visibleCards]);

  // Auto-fetch more when last page is partial and more data exists
  React.useEffect(() => {
    if (!showLoadMore || isLoadingMore || !onLoadMore) {
      return;
    }

    const lastPage = pages[pages.length - 1];
    const isLastPagePartial = lastPage && lastPage.length < visibleCards;

    if (isLastPagePartial) {
      void onLoadMore();
    }
  }, [pages, visibleCards, showLoadMore, isLoadingMore, onLoadMore]);

  if (callouts.length === 0) {
    return null;
  }

  const hasValidWidth = measuredWidth > 0;

  return (
    <div ref={containerRef} className={bleed ? '-mx-8 w-[calc(100%+4rem)]' : 'w-full'}>
      {hasValidWidth && (
        <Carousel.Root
          // Reset to page 0 when visibleCards changes
          key={visibleCards}
          className={cx('flex flex-col items-center gap-5', className)}
        >
          <CalloutCarouselContent
            onDismiss={onDismiss}
            showLoadMore={showLoadMore}
            pages={pages}
            containerWidth={containerWidth}
            edgePadding={edgePadding}
            cardGap={CARD_GAP}
            getCardWidthForPage={getCardWidthForPage}
            onCalloutClick={onCalloutClick}
          />
        </Carousel.Root>
      )}
    </div>
  );
};

export default CalloutCarouselBase;
